import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";
import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { TTableCustomCellRender } from "features/analytics/types/types";
import { formatCustomerCellLabel } from "features/analytics/utils/customerDisplay";
import { TChartData } from "Interfaces";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";

const STATUS_STYLES: Record<string, { bgcolor: string; color: string }> = {
  Completed: { bgcolor: "#E8F5E9", color: "#2E7D32" },
  Processing: { bgcolor: "#E3F2FD", color: "#1565C0" },
  "On Hold": { bgcolor: "#FFF3E0", color: "#E65100" },
  Pending: { bgcolor: "#FFF8E1", color: "#F57F17" },
  Paid: { bgcolor: "#E8F5E9", color: "#2E7D32" },
  Shipped: { bgcolor: "#E3F2FD", color: "#1565C0" },
  Delivered: { bgcolor: "#E8F5E9", color: "#2E7D32" },
  Cancelled: { bgcolor: "#FFEBEE", color: "#C62828" },
};

function formatOrderId(transactionId: string) {
  const trimmed = transactionId.trim();
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

function OrderStatusChip({ status }: { status: string }) {
  const styles = STATUS_STYLES[status] || {
    bgcolor: "rgba(0, 0, 0, 0.06)",
    color: "inherit",
  };

  return (
    <Chip
      label={status || "Not Set"}
      size="small"
      sx={{
        bgcolor: styles.bgcolor,
        color: styles.color,
        fontWeight: 600,
        borderRadius: 999,
        height: 28,
      }}
    />
  );
}

function StoreRecentOrders() {
  const { selectedProject } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();

  const getUserDashboardPath = useCallback(
    (leadId: number) => {
      if (!leadId || !selectedProject?.id || !selectedProject?.clientId) {
        return "";
      }

      return pathByName(RouteNames.USER_DASHBOARD_SINGLE, {
        clientId: selectedProject.clientId,
        projectId: selectedProject.id,
        leadid: leadId,
      });
    },
    [pathByName, selectedProject?.clientId, selectedProject?.id],
  );

  const customDataFn = useCallback(
    (data: TChartData[]) =>
      data.map((row) => ({
        [ApiDimension.DIMENSION_TRANSACTION_ID]:
          row[ApiDimension.DIMENSION_TRANSACTION_ID],
        [ApiDimension.DIMENSION_LEAD_ID]: Number(
          row[ApiDimension.DIMENSION_LEAD_ID],
        ),
        [ApiDimension.DIMENSION_EMAIL_ADDRESS]:
          row[ApiDimension.DIMENSION_EMAIL_ADDRESS],
        [ApiDimension.DIMENSION_LEAD_KEY]: row[ApiDimension.DIMENSION_LEAD_KEY],
        [ApiDimension.DIMENSION_SESSION_START_DATE]:
          row[ApiDimension.DIMENSION_SESSION_START_DATE],
        [ApiMetric.METRIC_SALE_ITEMS_QUANTITY]:
          row[ApiMetric.METRIC_SALE_ITEMS_QUANTITY],
        [ApiMetric.METRIC_SALES_REVENUE]: row[ApiMetric.METRIC_SALES_REVENUE],
        [ApiDimension.DIMENSION_SALE_ORDER_STATUS]:
          row[ApiDimension.DIMENSION_SALE_ORDER_STATUS],
        name: row.name,
        tick: row.tick,
      })),
    [],
  );

  const tableCustomHeaderRender = useMemo<TTableCustomCellRender>(
    () => ({
      [ApiDimension.DIMENSION_TRANSACTION_ID]: () => <>Order ID</>,
      [ApiDimension.DIMENSION_LEAD_ID]: () => <>Customer</>,
      [ApiDimension.DIMENSION_SESSION_START_DATE]: () => <>Date</>,
      [ApiMetric.METRIC_SALE_ITEMS_QUANTITY]: () => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "inherit",
          }}
        >
          <>Items Sold</>
        </span>
      ),
      [ApiMetric.METRIC_SALES_REVENUE]: () => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "inherit",
          }}
        >
          <>Total</>
        </span>
      ),
      [ApiDimension.DIMENSION_SALE_ORDER_STATUS]: () => <>Status</>,
    }),
    [],
  );

  const tableCustomCellRender = useMemo<TTableCustomCellRender>(
    () => ({
      [ApiDimension.DIMENSION_TRANSACTION_ID]: ({ value, data }) => {
        const leadId = Number(data?.[ApiDimension.DIMENSION_LEAD_ID] || 0);
        const label = formatOrderId(String(value || data?.name || ""));
        const href = getUserDashboardPath(leadId) || undefined;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {href ? (
              <MuiLink
                component={Link}
                to={href}
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {label}
              </MuiLink>
            ) : (
              <Typography fontWeight={600}>{label}</Typography>
            )}
          </Box>
        );
      },
      [ApiDimension.DIMENSION_LEAD_ID]: ({ value, data }) => {
        const leadId = Number(value);
        const label = formatCustomerCellLabel(
          data?.[ApiDimension.DIMENSION_EMAIL_ADDRESS],
          data?.[ApiDimension.DIMENSION_LEAD_KEY],
        );
        const href = getUserDashboardPath(leadId) || undefined;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {href ? (
              <MuiLink
                component={Link}
                to={href}
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {label}
              </MuiLink>
            ) : (
              <Typography fontWeight={600}>{label}</Typography>
            )}
          </Box>
        );
      },
      [ApiDimension.DIMENSION_SALE_ORDER_STATUS]: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
            width: "100%",
          }}
        >
          <OrderStatusChip status={String(value || "")} />
        </Box>
      ),
    }),
    [getUserDashboardPath],
  );

  const chart = useMemo(
    () => ({
      name: "Recent Orders",
      dimension: [
        ApiDimension.DIMENSION_TRANSACTION_ID,
        ApiDimension.DIMENSION_LEAD_ID,
        ApiDimension.DIMENSION_EMAIL_ADDRESS,
        ApiDimension.DIMENSION_LEAD_KEY,
        ApiDimension.DIMENSION_SESSION_START_DATE,
        ApiDimension.DIMENSION_SALE_ORDER_STATUS,
      ],
      template: ApiChartTemplate.TABLE,
      type: ApiChartType.TABLE,
      orderBy: `${ApiDimension.DIMENSION_SESSION_START_DATE} DESC`,
      attributes: [],
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_SALE_ITEMS_QUANTITY,
            ApiMetric.METRIC_SALES_REVENUE,
          ],
        },
      ],
      includeEmpty: true,
      having: [
        {
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_GREATER_THAN,
          value: 0,
        },
      ],
      displayOptions: {
        noAttributionToggle: true,
        noFilters: true,
        rowsPerPage: 0,
        disabledDimensionClick: true,
        disabledMetricClick: true,
        tableColWidths: {
          [ApiDimension.DIMENSION_TRANSACTION_ID]: 300,
          [ApiDimension.DIMENSION_LEAD_ID]: 300,
          [ApiMetric.METRIC_SALES_REVENUE]: 120,
          [ApiDimension.DIMENSION_SALE_ORDER_STATUS]: 140,
        },
      },
    }),
    [],
  );

  return (
    <AnalyticsCard
      noToolbar
      ignoreAttrModel
      forceNoComparison
      customDataFn={customDataFn}
      tableCustomCellRender={tableCustomCellRender}
      tableCustomHeaderRender={tableCustomHeaderRender}
      tableHiddenCols={[
        ApiDimension.DIMENSION_EMAIL_ADDRESS,
        ApiDimension.DIMENSION_LEAD_KEY,
      ]}
      chart={chart}
    />
  );
}

export default StoreRecentOrders;
